import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Fetch lineup submissions for users that the current user follows
 * for a specific league and event with pagination
 *
 * @param {string} leagueId - The league ID
 * @param {string} eventId - The event ID
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Number of submissions per page (default: 20)
 * @returns {Object} Query result with paginated submissions from followed users
 */
const fetchFollowingSubmissions = async (
  leagueId,
  eventId,
  page,
  limit,
  getAccessTokenSilently,
) => {
  const token = await getAccessTokenSilently();
  const apiUrl = process.env.REACT_APP_API_URL;

  const { data } = await axios.get(
    `${apiUrl}/api/lineup/${leagueId}/${eventId}/following?page=${page}&limit=${limit}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  // Handle different response formats:
  // 1. New format with success flag: { success: true, submissions: [...], pagination: {...} }
  // 2. Direct format: { submissions: [...], pagination: {...} }
  // 3. Old format: [...] (just an array)

  if (data.success && data.submissions && data.pagination) {
    // New format with success flag - extract submissions and pagination
    return {
      submissions: data.submissions,
      pagination: data.pagination,
    };
  }

  if (Array.isArray(data)) {
    // Old format - convert to new format
    return {
      submissions: data,
      pagination: {
        currentPage: page,
        totalPages: data.length > 0 ? 1 : 0,
        total: data.length,
        limit: limit,
        hasNext: false,
        hasPrevious: false,
      },
    };
  }

  // Direct format - return as is
  return data;
};

export const useFetchFollowingSubmissions = (
  leagueId,
  eventId,
  page = 1,
  limit = 20,
) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery(
    ["followingSubmissions", leagueId, eventId, page, limit],
    () =>
      fetchFollowingSubmissions(
        leagueId,
        eventId,
        page,
        limit,
        getAccessTokenSilently,
      ),
    {
      enabled: !!leagueId && !!eventId,
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false, // Prevent refetch on window focus to reduce flickering
      keepPreviousData: true, // Keep previous page data while loading next page
      onError: (error) => {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching following submissions:", error);
        }
      },
      onSuccess: () => {
        // Success - no logging needed
      },
    },
  );
};
